
import React, { useState, useEffect } from 'react';
import { InventoryItem } from '../types';
import { Button } from './Button';

interface ServiceItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: Omit<InventoryItem, 'id' | 'lastSyncedAt' | 'source'>, id?: string) => void;
  itemToEdit?: InventoryItem | null;
}

const initialFormDataInternal: Omit<InventoryItem, 'id' | 'lastSyncedAt' | 'source'> = {
  name: '',
  sku: '',
  category: '',
  stock: 0,
  cost: 0,
  price: 0, // Added price
  supplier: '',
  notes: '',
  usageType: 'general', // Added usageType
};

export const ServiceItemModal: React.FC<ServiceItemModalProps> = ({ isOpen, onClose, onSave, itemToEdit }) => {
  const [formData, setFormData] = useState(initialFormDataInternal);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof initialFormDataInternal, string>>>({});

  useEffect(() => {
    if (isOpen) {
      if (itemToEdit) {
        setFormData({
          name: itemToEdit.name,
          sku: itemToEdit.sku || '',
          category: itemToEdit.category || '',
          stock: itemToEdit.stock,
          cost: itemToEdit.cost || 0,
          price: itemToEdit.price || 0,
          supplier: itemToEdit.supplier || '',
          notes: itemToEdit.notes || '',
          usageType: itemToEdit.usageType || 'general',
        });
      } else {
        setFormData(initialFormDataInternal);
      }
      setErrors({});
    }
  }, [isOpen, itemToEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: string | number | undefined;
    if (type === 'number') {
        processedValue = parseFloat(value);
        if (isNaN(processedValue)) {
           // Allow clearing the field, or set to 0 if preferred.
           // If value is empty string, it becomes NaN. Let's treat empty as empty or 0.
           processedValue = value === '' ? undefined : 0; 
        }
    } else {
        processedValue = value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    if (errors[name as keyof typeof initialFormDataInternal]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof typeof initialFormDataInternal, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Item name is required.';
    if (typeof formData.stock !== 'number' || formData.stock < 0) newErrors.stock = 'Stock must be a non-negative number.';
    if (formData.cost !== undefined && (typeof formData.cost !== 'number' || formData.cost < 0)) newErrors.cost = 'Cost must be a non-negative number.';
    if (formData.price !== undefined && (typeof formData.price !== 'number' || formData.price < 0)) newErrors.price = 'Price must be a non-negative number.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Ensure numbers are numbers, not strings from input
      const dataToSave = {
        ...formData,
        stock: Number(formData.stock) || 0,
        cost: Number(formData.cost) || 0,
        price: Number(formData.price) || 0,
      };
      onSave(dataToSave, itemToEdit?.id);
    }
  };

  if (!isOpen) return null;

  const renderError = (field: keyof typeof initialFormDataInternal) => {
    return errors[field] ? <p className="text-red-500 text-xs mt-1">{errors[field]}</p> : null;
  };
  
  const inputClass = "mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] sm:text-sm";
  const labelClass = "block text-sm font-medium text-slate-700";


  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]"
      aria-labelledby="service-item-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 id="service-item-modal-title" className="text-xl font-semibold text-slate-800">
            {itemToEdit ? 'Edit Service Item' : 'Add New Service Item'}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700" aria-label="Close modal">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className={labelClass}>Item Name <span className="text-red-500">*</span></label>
            <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className={inputClass} required />
            {renderError('name')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sku" className={labelClass}>SKU (Optional)</label>
              <input type="text" name="sku" id="sku" value={formData.sku || ''} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="category" className={labelClass}>Category (Optional)</label>
              <input type="text" name="category" id="category" value={formData.category || ''} onChange={handleChange} className={inputClass} />
            </div>
          </div>
          
          <div>
            <label htmlFor="usageType" className={labelClass}>Usage Type</label>
            <select name="usageType" id="usageType" value={formData.usageType} onChange={handleChange} className={inputClass}>
              <option value="general">General Service Part</option>
              <option value="repair">Specific Repair Item</option>
              <option value="sale">Primarily for Sale</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="stock" className={labelClass}>Stock Quantity <span className="text-red-500">*</span></label>
              <input type="number" name="stock" id="stock" value={formData.stock} onChange={handleChange} className={inputClass} required min="0" />
              {renderError('stock')}
            </div>
            <div>
              <label htmlFor="cost" className={labelClass}>Cost Price</label>
              <input type="number" name="cost" id="cost" value={formData.cost === undefined ? '' : formData.cost} onChange={handleChange} className={inputClass} step="0.01" min="0" />
              {renderError('cost')}
            </div>
            <div>
              <label htmlFor="price" className={labelClass}>Sale Price</label>
              <input type="number" name="price" id="price" value={formData.price === undefined ? '' : formData.price} onChange={handleChange} className={inputClass} step="0.01" min="0" />
              {renderError('price')}
            </div>
          </div>
          
          <div>
            <label htmlFor="supplier" className={labelClass}>Supplier (Optional)</label>
            <input type="text" name="supplier" id="supplier" value={formData.supplier || ''} onChange={handleChange} className={inputClass} />
          </div>

          <div>
            <label htmlFor="notes" className={labelClass}>Notes (Optional)</label>
            <textarea name="notes" id="notes" rows={3} value={formData.notes || ''} onChange={handleChange} className={inputClass}></textarea>
          </div>

          <div className="border-t border-slate-200 pt-4 flex justify-end space-x-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary">{itemToEdit ? 'Save Changes' : 'Add Item'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};
